import type { MaybeRefOrGetter } from "vue";
import { useApi } from "@api/integration";
import type {
  OfertaEnergiaRetribucionesColaboradoresListItemModelPagedResultModel,
  OfertaEnergiaRetribucionesColaboradoresListItemModel,
  OfertasEnergiaRetribucionesListFiltrosModel,
} from "@api/api";
import { useInfiniteScrollPaged } from "@/composables/useInfiniteScrollPaged";

export interface UseOfertasEnergiaRetribucionListInfiniteScrollOptions {
  pageSize?: number;
}

export function useOfertasEnergiaRetribucionListInfiniteScroll(
  filtrosSource: MaybeRefOrGetter<OfertasEnergiaRetribucionesListFiltrosModel | null>,
  options: UseOfertasEnergiaRetribucionListInfiniteScrollOptions = {},
) {
  const api = useApi();
  const pageSize = options.pageSize ?? 15;

  return useInfiniteScrollPaged<OfertaEnergiaRetribucionesColaboradoresListItemModel, OfertasEnergiaRetribucionesListFiltrosModel>(filtrosSource, {
    pageSize,
    fetchPage: async ({ skip, take, filtros, append }) => {
      if (!filtros.Comercializadora) {
        return { items: [], hasMore: false, totalItems: 0 };
      }

      const page = await api.callStandardApi(
        "getOfertasEnergiaRetribucionesColaboradoresList",
        { showLoading: false },
        take,
        skip,
        filtros.Comercializadora ?? undefined,
        filtros.TipoEnergia ?? undefined,
        filtros.TipoPersona ?? undefined,
        filtros.ConAutoconsumo ?? undefined,
        filtros.PotenciaMin ?? undefined,
        filtros.PotenciaMax ?? undefined,
      );

      const batch = readItemsBatch(page);
      const hasMore = readHasNextPageFlag(page);
      const totalItems = page.TotalCount ?? 0;
      return { items: batch, hasMore, totalItems };
    },
    fetchUnpaged: async (filtros) => {
      if (!filtros.Comercializadora) {
        return { items: [], totalItems: 0 };
      }

      const page = await api.callStandardApi(
        "getOfertasEnergiaRetribucionesColaboradoresList",
        { showLoading: true },
        undefined,
        undefined,
        filtros.Comercializadora ?? undefined,
        filtros.TipoEnergia ?? undefined,
        filtros.TipoPersona ?? undefined,
        filtros.ConAutoconsumo ?? undefined,
        filtros.PotenciaMin ?? undefined,
        filtros.PotenciaMax ?? undefined
      );
      const batch = readItemsBatch(page);
      const totalItems = page.TotalCount ?? 0;
      return { items: batch, totalItems };
    },
  });
}

function normalizeStrFilter(value: string | null | undefined): string | undefined {
  if (value == null || value === "") {
    return undefined;
  }
  return value;
}

function toDateOnlyQueryParam(value: Date | null | undefined): string | undefined {
  if (value == null) {
    return undefined;
  }
  return value.toISOString().split("T")[0];
}

function readItemsBatch(page: OfertaEnergiaRetribucionesColaboradoresListItemModelPagedResultModel | undefined): OfertaEnergiaRetribucionesColaboradoresListItemModel[] {
  if (!page) {
    return [];
  }
  return page.Items ?? [];
}

function readHasNextPageFlag(page: OfertaEnergiaRetribucionesColaboradoresListItemModelPagedResultModel | undefined): boolean {
  if (!page) {
    return false;
  }
  return page.HasNextPage;
}
